import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { venueUrl } from "../../utils/venueUrl";
import { API, formatApiErrorDetail } from "../../App";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "../../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../../components/ui/table";
import { Search, Plus, Edit, Star, Eye, EyeOff, Building2, Archive, ArchiveRestore, Trash2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { getLocalized } from "../../utils/localized";
import { BilingualInput, BilingualTextarea } from "../../components/BilingualField";
import { specialtyLabelKey, filterSpecialtiesByVenueType } from "../../utils/venueSpecialty";

export default function AdminVenues() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [venues, setVenues] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [districts, setDistricts] = useState([]);
  const [venueTypes, setVenueTypes] = useState([]);
  const [cuisines, setCuisines] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [users, setUsers] = useState([]);
  const [typeFilter, setTypeFilter] = useState("all");
  const [districtFilter, setDistrictFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("active");
  const [archiveTarget, setArchiveTarget] = useState(null); // {venue, mode: 'archive'|'restore'}
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const buildParams = () => {
    const params = new URLSearchParams();
    if (search.trim()) params.set("search", search.trim());
    if (typeFilter !== "all") params.set("venue_type_id", typeFilter);
    if (districtFilter !== "all") params.set("district_id", districtFilter);
    params.set("status", statusFilter);
    return params.toString();
  };

  const refreshVenues = async () => {
    try {
      const res = await axios.get(`${API}/admin/venues?${buildParams()}`);
      setVenues(res.data.venues || []);
      setTotal(res.data.total || 0);
    } catch {}
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [venuesRes, districtsRes, typesRes, cuisinesRes, usersRes] = await Promise.all([
          axios.get(`${API}/admin/venues?${buildParams()}`),
          axios.get(`${API}/taxonomy/districts`),
          axios.get(`${API}/taxonomy/venue-types`),
          axios.get(`${API}/taxonomy/cuisines`),
          axios.get(`${API}/admin/users`),
        ]);
        setVenues(venuesRes.data.venues || []);
        setTotal(venuesRes.data.total || 0);
        setDistricts(districtsRes.data);
        setVenueTypes(typesRes.data);
        setCuisines(cuisinesRes.data);
        setUsers(usersRes.data.users || []);
      } catch (error) {
        console.error("Error fetching venues:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, typeFilter, districtFilter, statusFilter]);

  const handleToggleFeatured = async (venueId, currentValue) => {
    try {
      await axios.put(`${API}/admin/venues/${venueId}`, { is_featured: !currentValue });
      setVenues(venues.map(v => v.venue_id === venueId ? { ...v, is_featured: !currentValue } : v));
      toast.success(currentValue ? t("admin_venues:toast_unfeatured") : t("admin_venues:toast_featured"));
    } catch {
      toast.error(t("admin_venues:toast_update_failed"));
    }
  };

  const handleToggleActive = async (venueId, currentValue) => {
    try {
      await axios.put(`${API}/admin/venues/${venueId}`, { is_active: !currentValue });
      setVenues(venues.map(v => v.venue_id === venueId ? { ...v, is_active: !currentValue } : v));
      toast.success(currentValue ? t("admin_venues:toast_deactivated") : t("admin_venues:toast_activated"));
    } catch {
      toast.error(t("admin_venues:toast_update_failed"));
    }
  };

  const handleArchive = async () => {
    if (!archiveTarget) return;
    setActionLoading(true);
    const { venue, mode } = archiveTarget;
    try {
      await axios.put(`${API}/admin/venues/${venue.venue_id}/archive`, {
        is_archived: mode === "archive",
      });
      toast.success(mode === "archive" ? t("admin_venues:toast_archived") : t("admin_venues:toast_unarchived"));
      setArchiveTarget(null);
      await refreshVenues();
    } catch {
      toast.error(t("admin_venues:toast_archive_failed"));
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setActionLoading(true);
    try {
      await axios.delete(`${API}/admin/venues/${deleteTarget.venue_id}`);
      toast.success(t("admin_venues:toast_deleted"));
      setDeleteTarget(null);
      await refreshVenues();
    } catch {
      toast.error(t("admin_venues:toast_delete_failed"));
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div data-testid="admin-venues-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-medium text-foreground flex items-center gap-2">
          <Building2 className="h-6 w-6 text-primary" />
          {t("admin_venues:title")} ({total})
        </h1>
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button data-testid="admin-create-venue-btn">
              <Plus className="h-4 w-4 mr-2" />
              {t("admin_venues:add_venue")}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{t("admin_venues:create_new_venue")}</DialogTitle>
            </DialogHeader>
            <CreateVenueForm
              districts={districts}
              venueTypes={venueTypes}
              cuisines={cuisines}
              onSuccess={() => {
                setShowCreateDialog(false);
                refreshVenues();
              }}
            />
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-card rounded-xl border border-border mb-6 p-4 flex flex-col lg:flex-row gap-3 lg:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t("admin_venues:search_placeholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="admin-venue-search"
          />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-44" data-testid="admin-venue-type-filter">
            <SelectValue placeholder={t("admin_venues:filter_by_type")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("admin_venues:all_types")}</SelectItem>
            {venueTypes.map((vt) => (
              <SelectItem key={vt.id} value={vt.id}>{getLocalized(vt, "name", lang) || vt.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={districtFilter} onValueChange={setDistrictFilter}>
          <SelectTrigger className="w-44" data-testid="admin-venue-district-filter">
            <SelectValue placeholder={t("admin_venues:filter_by_city", { defaultValue: "Filter by district" })} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("admin_venues:all_cities", { defaultValue: "All Districts" })}</SelectItem>
            {districts.map((d) => (
              <SelectItem key={d.id} value={d.id}>{getLocalized(d, "name", lang) || d.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-52" data-testid="admin-venue-status-filter">
            <SelectValue placeholder={t("admin_venues:filter_by_status")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active">{t("admin_venues:status_active")}</SelectItem>
            <SelectItem value="inactive">{t("admin_venues:status_inactive")}</SelectItem>
            <SelectItem value="archived">{t("admin_venues:status_archived")}</SelectItem>
            <SelectItem value="all">{t("admin_venues:status_all")}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="space-y-2">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="animate-pulse bg-muted h-16 rounded-lg" />
          ))}
        </div>
      ) : (
        <div className="bg-card rounded-xl border border-border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("admin_venues:col_name")}</TableHead>
                <TableHead>{t("admin_venues:col_type")}</TableHead>
                <TableHead>{t("admin_venues:col_city")}</TableHead>
                <TableHead>{t("admin_venues:col_featured")}</TableHead>
                <TableHead>{t("admin_venues:col_active")}</TableHead>
                <TableHead className="text-right">{t("admin_venues:col_actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {venues.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-10 text-muted-foreground">{t("admin_venues:no_venues")}</TableCell></TableRow>
              ) : venues.map((venue) => {
                const vt = venueTypes.find(t2 => t2.id === venue.venue_type_id);
                const dist = districts.find(d => d.id === (venue.district_id || venue.city_id));
                const archived = !!venue.is_archived;
                return (
                <TableRow key={venue.venue_id} data-testid={`admin-venue-row-${venue.venue_id}`} className={archived ? "opacity-60" : ""}>
                  <TableCell className="font-medium">
                    {getLocalized(venue, "name", lang) || venue.name}
                    {archived && <span className="ml-2 px-1.5 py-0.5 text-xs rounded bg-muted text-muted-foreground">{t("admin_venues:badge_archived")}</span>}
                  </TableCell>
                  <TableCell>{vt ? (getLocalized(vt, "name", lang) || vt.name) : "\u2014"}</TableCell>
                  <TableCell>{dist ? (getLocalized(dist, "name", lang) || dist.name) : "\u2014"}</TableCell>
                  <TableCell>
                    <Button variant="ghost" size="sm" onClick={() => handleToggleFeatured(venue.venue_id, venue.is_featured)} title={t("admin_venues:action_toggle_featured")}>
                      {venue.is_featured ? (
                        <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                      ) : (
                        <Star className="h-4 w-4 text-muted-foreground" />
                      )}
                    </Button>
                  </TableCell>
                  <TableCell>
                    <Button variant="ghost" size="sm" onClick={() => handleToggleActive(venue.venue_id, venue.is_active)} title={t("admin_venues:action_toggle_active")}>
                      {venue.is_active ? (
                        <Eye className="h-4 w-4 text-green-500" />
                      ) : (
                        <EyeOff className="h-4 w-4 text-muted-foreground" />
                      )}
                    </Button>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="sm" asChild data-testid={`edit-venue-${venue.venue_id}`} title={t("admin_venues:action_edit")}>
                        <Link to={`/admin/venues/${venue.venue_id}/edit`}>
                          <Edit className="h-4 w-4" />
                        </Link>
                      </Button>
                      <Button variant="ghost" size="sm" asChild title={t("admin_venues:action_view")}>
                        <Link to={venueUrl(venue)} target="_blank">
                          <Eye className="h-4 w-4" />
                        </Link>
                      </Button>
                      {archived ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setArchiveTarget({ venue, mode: "restore" })}
                          title={t("admin_venues:action_unarchive")}
                          data-testid={`unarchive-venue-${venue.venue_id}`}
                        >
                          <ArchiveRestore className="h-4 w-4 text-emerald-600" />
                        </Button>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setArchiveTarget({ venue, mode: "archive" })}
                          title={t("admin_venues:action_archive")}
                          data-testid={`archive-venue-${venue.venue_id}`}
                        >
                          <Archive className="h-4 w-4 text-amber-600" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDeleteTarget(venue)}
                        title={t("admin_venues:action_delete")}
                        data-testid={`delete-venue-${venue.venue_id}`}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Archive / Restore confirmation */}
      <AlertDialog open={!!archiveTarget} onOpenChange={(open) => !open && setArchiveTarget(null)}>
        <AlertDialogContent data-testid="archive-venue-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {archiveTarget?.mode === "archive"
                ? t("admin_venues:archive_dialog_title")
                : t("admin_venues:unarchive_dialog_title")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {archiveTarget?.mode === "archive"
                ? t("admin_venues:archive_dialog_desc")
                : t("admin_venues:unarchive_dialog_desc")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actionLoading}>{t("admin_venues:cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleArchive} disabled={actionLoading} data-testid="confirm-archive-venue">
              {archiveTarget?.mode === "archive" ? t("admin_venues:archive") : t("admin_venues:unarchive")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-venue-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("admin_venues:delete_dialog_title")}</AlertDialogTitle>
            <AlertDialogDescription>{t("admin_venues:delete_dialog_desc")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actionLoading}>{t("admin_venues:cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={actionLoading} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-venue">
              {t("admin_venues:delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function CreateVenueForm({ districts, venueTypes, cuisines, onSuccess }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "", name_el: "",
    slug: "",
    venue_type_id: "", district_id: "",
    address: "", address_el: "",
  });
  // Track whether the admin has manually edited the slug. While untouched,
  // the slug auto-rebuilds from the venue name on every change.
  const [slugTouched, setSlugTouched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const localizedName = (item) => {
    if (!item) return "";
    return (lang === "el" && item.name_el) || item.name || "";
  };

  const generateSlug = (text) =>
    (text || "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "") // strip diacritics (accents)
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");

  // Compose the venue slug from the venue name only — Phase 1 spec requires
  // unique-per-(district+area) slugs; suffixes are added manually by the
  // admin if the backend returns 409 on collision.
  useEffect(() => {
    if (slugTouched) return;
    const nameSlug = generateSlug(form.name);
    setForm((f) => ({ ...f, slug: nameSlug }));
  }, [form.name, slugTouched]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const payload = { ...form };
      if (!payload.address_el) delete payload.address_el;
      if (!payload.name_el) delete payload.name_el;
      const res = await axios.post(`${API}/admin/venues`, payload);
      toast.success(t("admin_venues:toast_venue_created"));
      onSuccess?.();
      // Redirect to the full edit page so admin can fill in everything else
      // (descriptions, specialties chips, dietary tags, opening hours, gallery, menu...)
      if (res.data?.venue_id) {
        navigate(`/admin/venues/${res.data.venue_id}/edit`);
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5" data-testid="create-venue-form">
      {error && <div className="bg-destructive/10 text-destructive px-4 py-3 rounded-lg text-sm">{error}</div>}

      <BilingualInput
        label={t("admin_venue_edit:field_name", { defaultValue: "Venue Name" }) + " *"}
        value={form.name}
        valueEl={form.name_el}
        onChange={(e) => setForm({ ...form, name: e.target.value })}
        onChangeEl={(e) => setForm({ ...form, name_el: e.target.value })}
        data-testid="create-venue-name"
      />

      <div>
        <Label>{t("admin_venue_edit:field_slug", { defaultValue: "URL Slug" })} *</Label>
        <Input
          value={form.slug}
          onChange={(e) => {
            const v = e.target.value;
            // Empty field → resume auto-fill on next name/city change.
            if (v === "") setSlugTouched(false);
            else setSlugTouched(true);
            setForm({ ...form, slug: v });
          }}
          required
          className="mt-1 font-mono text-sm"
          placeholder="english-venue-name"
          data-testid="create-venue-slug"
        />
        <p className="text-[11px] text-muted-foreground mt-1">
          {slugTouched
            ? t("admin_venue_edit:slug_manual_hint", { defaultValue: "Custom slug — auto-fill paused. Clear the field to resume." })
            : t("admin_venue_edit:slug_auto_hint", { defaultValue: "Auto-filled from the venue name. Edit to override or add a suffix on collisions." })}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>{t("admin_venue_edit:field_venue_type", { defaultValue: "Venue Type" })} *</Label>
          <Select value={form.venue_type_id} onValueChange={(v) => setForm({ ...form, venue_type_id: v })}>
            <SelectTrigger className="mt-1" data-testid="create-venue-type">
              <SelectValue placeholder={t("admin_venue_edit:select_type", { defaultValue: "Select type" })} />
            </SelectTrigger>
            <SelectContent>
              {venueTypes.map((vt) => (
                <SelectItem key={vt.id} value={vt.id}>{localizedName(vt)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>{t("admin_venue_edit:field_city", { defaultValue: "District" })} *</Label>
          <Select value={form.district_id} onValueChange={(v) => setForm({ ...form, district_id: v })}>
            <SelectTrigger className="mt-1" data-testid="create-venue-district">
              <SelectValue placeholder={t("admin_venue_edit:select_city", { defaultValue: "Select district" })} />
            </SelectTrigger>
            <SelectContent>
              {districts.map((d) => (
                <SelectItem key={d.id} value={d.id}>{localizedName(d)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <BilingualInput
        label={t("admin_venue_edit:field_street_address", { defaultValue: "Street Address" })}
        value={form.address}
        valueEl={form.address_el}
        onChange={(e) => setForm({ ...form, address: e.target.value })}
        onChangeEl={(e) => setForm({ ...form, address_el: e.target.value })}
        data-testid="create-venue-address"
      />

      <p className="text-xs text-muted-foreground bg-muted/30 px-3 py-2 rounded-lg">
        {t("admin_venues:create_then_edit_hint", { defaultValue: "After creation you'll be redirected to the full editor to add specialties, dietary tags, descriptions, opening hours, gallery, menu and more." })}
      </p>

      <Button type="submit" disabled={loading} className="w-full" data-testid="create-venue-submit">
        {loading ? t("admin_venues:creating") : t("admin_venues:create_venue_btn")}
      </Button>
    </form>
  );
}
